import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { first, forkJoin } from 'rxjs';
import { TagService } from '@app/_components/tag/tag.service';
import { OrderProductService } from '@app/_components/order-product';
import { AlertService } from '@app/_services';

interface UncoveredOp {
	orderProductId: number;
	name: string;
}

/**
 * OP coverage audit. Lists every order product that has no reservation tag
 * (tagType=8) wired via {@code task_tag.orderProductId}, with a one-click
 * "Wire a tag" action that hands the OP id back to the parent (TagList)
 * which then opens the standard tag dialog with the OP pre-selected.
 *
 * <p>This is the only OP-centric view in the app — the Tag List is tag-centric
 * and structurally can't show OPs that have no tag row pointing at them. So
 * this audit fills the one genuine gap that the previous separate
 * "OP → Tag Mapping" page used to fill (Coverage sub-view), without dragging
 * along the redundant CRUD it also offered.
 */
@Component({
	selector: 'app-op-coverage-dialog',
	templateUrl: './op-coverage-dialog.component.html',
	styleUrls: ['./op-coverage-dialog.component.css'],
	standalone: false
})
export class OpCoverageDialogComponent implements OnInit
{
	loading = false;
	uncoveredOps: UncoveredOp[] = [];
	searchTerm = '';

	/** Fired when the admin clicks "Wire a tag" for an OP. Parent opens the
	 *  tag dialog pre-filled with this orderProductId. */
	@Output() wireTagFor = new EventEmitter<number>();

	constructor(
		private dialogRef: MatDialogRef<OpCoverageDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private tagService: TagService,
		private orderProductService: OrderProductService,
		private alertService: AlertService,
	) {}

	ngOnInit(): void
	{
		this.loadCoverage();
	}

	private loadCoverage(): void
	{
		this.loading = true;
		// Tag side: only tagType=8 (reservation tags) can wire to an OP. Page size
		// 1000 dodges the default-20 server pagination; safe upper bound for KBM.
		// OP side: /find-all is the unpaginated endpoint — required so we don't
		// miss OPs past the first page.
		forkJoin({
			tags: this.tagService.findByFilter(0, 1000, 'name', 1, null, null, { tagType: { value: 8 } }),
			ops:  this.orderProductService.findAllOrderProducts()
		}).pipe(first()).subscribe({
			next: ({ tags: tagsRes, ops: opsRes }: any) =>
			{
				const tags = tagsRes?.data?.tags || [];
				const opsList = opsRes?.data?.orderProducts
					|| opsRes?.data?.orderProductList
					|| opsRes?.data?.orderProductVOList
					|| opsRes?.data?.list
					|| [];
				const wiredOpIds = new Set<number>(
					tags.filter((t: any) => t.orderProductId != null).map((t: any) => t.orderProductId)
				);
				this.uncoveredOps = opsList
					.filter((op: any) => op && op.orderProductId != null)
					.filter((op: any) => !wiredOpIds.has(op.orderProductId))
					.map((op: any) => ({ orderProductId: op.orderProductId, name: op.name || '(unnamed)' }))
					.sort((a: UncoveredOp, b: UncoveredOp) => a.name.localeCompare(b.name));
			},
			error: (err: any) =>
			{
				console.log('OpCoverageDialog loadCoverage FAIL: ' + JSON.stringify(err));
				this.alertService.error(err);
				this.loading = false;
			},
			complete: () => { this.loading = false; }
		});
	}

	get filteredUncoveredOps(): UncoveredOp[]
	{
		const t = this.searchTerm.trim().toLowerCase();
		if (!t) return this.uncoveredOps;
		return this.uncoveredOps.filter(op =>
			op.name.toLowerCase().includes(t) || String(op.orderProductId).includes(t));
	}

	onWireTag(op: UncoveredOp): void
	{
		// Hand the OP id to the parent component, then close. The parent opens
		// the tag dialog (single CRUD path) with orderProductId pre-filled.
		this.wireTagFor.emit(op.orderProductId);
		this.dialogRef.close();
	}

	close(): void
	{
		this.dialogRef.close();
	}
}
